import { FunctionComponent, useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { LoadingOverlay, Modal } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { FileTypes } from "@/pages/Private/pages/Courses/enums/FileTypes.enum";
import {
	useGetMediaFoldersQuery,
	useGetMediaLibraryQuery,
	useUploadToLibraryMutation,
} from "@/pages/Private/redux/media/media.api";
import { LibraryAsset } from "@/pages/Private/redux/media/media.schema";
import { classNames } from "@/utils/classNames";

import {
	FolderBreadcrumb,
	FolderSelection,
	MediaFolderCard,
	MediaFolderTree,
	childrenOf,
	folderIdOf,
} from "./MediaFolderTree";

/**
 * What was chosen, in both the forms a caller might need it.
 *
 * Phase content wants the URL and nothing else, because a phase holds a URL. The scenario's
 * thumbnail, large image and trailer want a `course-file` with the right assignment, which
 * means the library has to be told *which* entry — hence the kind and the id travelling
 * alongside.
 */
export interface MediaSelection {
	url: string;
	kind: "generated" | "uploaded";
	id: number;
}

interface MediaPickerModalProps {
	courseId: number;
	/** Only files this phase could actually hold are offered. */
	contentType: FileTypes;
	/**
	 * Only pictures that define a graphical style — the style step's picker. Uploading from
	 * here still works: the caller analyses the pick, which is what makes it a style.
	 */
	styleReferencesOnly?: boolean;
	isOpened: boolean;
	handleClose: () => void;
	onSelect: (selection: MediaSelection) => void;
}

/** Which mime prefixes a phase of this content type can hold. */
const ACCEPTS: Partial<Record<FileTypes, string>> = {
	[FileTypes.Image]: "image/",
	[FileTypes.Video]: "video/",
	[FileTypes.Audio]: "audio/",
};

/**
 * Picking a file for a phase, out of everything the company has.
 *
 * Phase content is a URL on the phase and nothing else — no course-file, no join — so this
 * returns a URL and the phase stores it. That is also why the library's own cards do not
 * offer "use as content": there is no phase in that context to put it on, so the button
 * could only ever have created a course-file nothing pointed at.
 *
 * The upload lives here rather than in the phase editor so that everything uploaded lands in
 * the library and can be used again. Uploading straight into one phase is how files became
 * unreachable in the first place.
 *
 * The folder tree is the library's own, read-only: folders help where a file is *picked*,
 * not only where it is browsed, but organising them stays on the library page.
 */
export const MediaPickerModal: FunctionComponent<MediaPickerModalProps> = ({
	courseId,
	contentType,
	styleReferencesOnly = false,
	isOpened,
	handleClose,
	onSelect,
}) => {
	const { t } = useTranslation();
	const ts = useCallback(
		(key: string, options?: Record<string, unknown>) =>
			t(`courses.mediaLibrary.${key}`, options ?? {}),
		[t]
	);

	const [scope, setScope] = useState<"scenario" | "company">("scenario");
	const [folder, setFolder] = useState<FolderSelection>("root");
	// Flattened by default: a picker's first question is "what is there to pick", so the
	// root offers every file, and the folders narrow it from there. The pill turns it off.
	const [includeSubfolders, setIncludeSubfolders] = useState(true);
	const [errorMessage, setErrorMessage] = useState("");
	const fileInputRef = useRef<HTMLInputElement>(null);

	const { data: assets, isFetching } = useGetMediaLibraryQuery(
		{
			...(scope === "scenario" ? { courseId } : {}),
			folderId: folderIdOf(folder),
			includeDescendants: includeSubfolders ? true : undefined,
		},
		// Refetched on every open: generations finish in the background and nothing
		// invalidates this cache, so a picker opened later must not show an older library.
		{ skip: !isOpened, refetchOnMountOrArgChange: true }
	);
	const { data: tree } = useGetMediaFoldersQuery(undefined, {
		skip: !isOpened,
		refetchOnMountOrArgChange: true,
	});
	const folders = tree?.folders ?? [];
	const subfolders = childrenOf(folders, folder);
	const [upload, { isLoading: isUploading }] = useUploadToLibraryMutation();

	const prefix = ACCEPTS[contentType];
	const candidates = useMemo(
		() =>
			(assets ?? []).filter(
				(asset) =>
					(!prefix || (asset.mimeType ?? "").startsWith(prefix)) &&
					(!styleReferencesOnly || asset.styleReference)
			),
		[assets, prefix, styleReferencesOnly]
	);

	const handleUpload = async (file: File) => {
		setErrorMessage("");

		try {
			const created = await upload({ courseId, file }).unwrap();

			// Straight onto the phase: uploading from here is "I want this one", not merely
			// "put this somewhere". It stays in the library either way.
			if (created.fullFilePath && created.id) {
				// A freshly uploaded file is an uploaded library entry, and its course-file id
				// is that entry's id — so a caller that needs to assign it can, immediately.
				onSelect({ url: created.fullFilePath, kind: "uploaded", id: created.id });
				handleClose();
			}
		} catch {
			setErrorMessage(ts("uploadFailed"));
		}
	};

	return (
		<Modal
			handleClose={handleClose}
			isOpened={isOpened}
			// Choosing a card *is* the action, so there is no save button to press.
			showActions={false}
			size="lg"
			title={ts(styleReferencesOnly ? "pickStyleTitle" : "pickTitle")}
		>
			<div className="space-y-3">
				<div className="flex flex-wrap items-center justify-between gap-3">
					<div className="flex gap-1">
						{(["scenario", "company"] as const).map((option) => (
							<button
								key={option}
								aria-pressed={scope === option}
								className={classNames(
									"rounded-full border px-3 py-1.5 text-xs font-semibold",
									scope === option
										? "border-accent bg-accent-tint text-ink"
										: "border-border-soft bg-white text-subtle"
								)}
								type="button"
								onClick={() => setScope(option)}
							>
								{ts(option === "scenario" ? "scopeScenario" : "scopeCompany")}
							</button>
						))}
					</div>

					<div className="flex items-center gap-2">
						<input
							ref={fileInputRef}
							accept={prefix ? `${prefix}*` : undefined}
							className="hidden"
							type="file"
							onChange={(event) => {
								const file = event.target.files?.[0];

								if (file) {
									void handleUpload(file);
								}

								event.target.value = "";
							}}
						/>
						<Button
							color={ButtonColor.BRAND}
							disabled={isUploading}
							size={ButtonSize.S}
							title={ts("uploadNew")}
							type="button"
							onClick={() => fileInputRef.current?.click()}
						/>
					</div>
				</div>

				{!!errorMessage && (
					<p className="rounded-card bg-error-light px-3 py-2 text-xs text-text">{errorMessage}</p>
				)}

				{(isFetching || isUploading) && <LoadingOverlay />}

				<div className="flex gap-3">
					{/* Only once there are folders: with none, the two fixed rows would be noise. */}
					{folders.length > 0 && (
						<aside className="max-h-[60vh] w-48 shrink-0 overflow-y-auto">
							<MediaFolderTree folders={folders} selected={folder} onSelect={setFolder} />
						</aside>
					)}

					<div className="min-w-0 flex-1 space-y-3">
						{folders.length > 0 && (
							<FolderBreadcrumb
								folders={folders}
								includeSubfolders={includeSubfolders}
								selected={folder}
								onIncludeSubfolders={setIncludeSubfolders}
								onSelect={setFolder}
							/>
						)}

						{!isFetching && !candidates.length && !subfolders.length && (
							<p className="rounded-card border border-dashed border-border bg-surface px-4 py-10 text-center text-sm text-subtle">
								{ts(
									typeof folder === "number"
										? "folders.emptyFolder"
										: styleReferencesOnly
											? "pickEmptyStyles"
											: "pickEmpty"
								)}
							</p>
						)}

						<div className="max-h-[60vh] space-y-3 overflow-y-auto">
							{subfolders.length > 0 && (
								<div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-4">
									{subfolders.map((child) => (
										<MediaFolderCard key={child.id} folder={child} onOpen={setFolder} />
									))}
								</div>
							)}

							<div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-4">
								{candidates.map((asset: LibraryAsset) => (
									<button
										key={`${asset.kind}-${asset.id}`}
										className="flex flex-col gap-1.5 rounded-card border border-border-soft bg-white p-2 text-left hover:border-accent"
										type="button"
										onClick={() => {
											onSelect({ url: asset.fullFilePath, kind: asset.kind, id: asset.id });
											handleClose();
										}}
									>
										<span className="flex h-24 items-center justify-center overflow-hidden rounded-card bg-surface">
											{(asset.mimeType ?? "").startsWith("image/") ? (
												<img
													alt={asset.fileName ?? ""}
													className="max-h-24 w-full object-contain"
													src={asset.fullFilePath}
												/>
											) : (
												<video
													className="max-h-24 w-full"
													preload="metadata"
													src={asset.fullFilePath}
												/>
											)}
										</span>
										<span
											className="truncate text-xs font-semibold text-ink"
											title={asset.fileName ?? ""}
										>
											{asset.fileName}
										</span>
										<span className="truncate text-xs text-subtle">
											{asset.styleReference
												? ts("origin.styleReference")
												: asset.jobType
													? t(`courses.jobsCosts.type.${asset.jobType}`)
													: ts("origin.uploaded")}
										</span>
									</button>
								))}
							</div>
						</div>
					</div>
				</div>
			</div>
		</Modal>
	);
};
